import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import apiService from '../services/api.service';
import { toast } from 'sonner';

interface KartCode {
  id: string;
  code: string;
  recipient: string;
  created_at: string;
  redeemed_email: string | null;
  redeemed_at: string | null;
}

const AdminKartCodes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [codes, setCodes] = useState<KartCode[]>([]);
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await apiService.get('/kart/admin/codes');
      setCodes(res.data.codes || []);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('kartCodes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const res = await apiService.post('/kart/admin/codes', { recipients: recipients.split('\n') });
      setRecipients('');
      toast.success(t('kartCodes.created', { count: res.data.codes.length }));
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('kartCodes.createError'));
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('kartCodes.copied'));
    } catch {
      toast.error(t('kartCodes.copyError'));
    }
  };

  const free = codes.filter((c) => !c.redeemed_at);
  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <p className="text-sm text-muted-foreground">{t('kartCodes.intro')}</p>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="max-w-xl space-y-2">
        <Label htmlFor="kart-recipients">{t('kartCodes.recipients')}</Label>
        <Textarea
          id="kart-recipients"
          rows={4}
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder={t('kartCodes.recipientsHint')}
        />
        <Button onClick={create} disabled={creating || !recipients.trim()}>
          {creating && <Loader2 size={16} className="mr-2 animate-spin" />}
          {t('kartCodes.create')}
        </Button>
      </div>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-muted-foreground" />
      ) : codes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('kartCodes.empty')}</p>
      ) : (
        <div className="space-y-3">
          <Button
            variant="secondary"
            disabled={free.length === 0}
            onClick={() => copy(free.map((c) => `${c.recipient}\t${c.code}`).join('\n'))}
          >
            {t('kartCodes.copyFree', { count: free.length })}
          </Button>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('kartCodes.recipient')}</TableHead>
                  <TableHead>{t('kartCodes.code')}</TableHead>
                  <TableHead>{t('kartCodes.createdAt')}</TableHead>
                  <TableHead>{t('kartCodes.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.recipient}</TableCell>
                    <TableCell className="font-mono whitespace-nowrap">
                      {c.code}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-1 h-7 w-7"
                        aria-label={t('kartCodes.copy')}
                        onClick={() => copy(c.code)}
                      >
                        <Copy size={14} />
                      </Button>
                    </TableCell>
                    <TableCell>{date(c.created_at)}</TableCell>
                    <TableCell>
                      {c.redeemed_at
                        ? t('kartCodes.redeemed', { email: c.redeemed_email || '—', date: date(c.redeemed_at) })
                        : t('kartCodes.free')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminKartCodes;
